import {useGetPuck} from "@puckeditor/core";
import {useCallback, useRef, type CSSProperties} from "react";
import {normalizeStyle, parseTemplateText} from "./serialization";
import type {BlockStyle, InputDefinitions, ParagraphSegment} from "./types";
import {paragraphVariables} from "./variables";

type PuckListState = {
  data?: { content?: unknown[]; zones?: Record<string, unknown[] | undefined> };
  indexes?: { zones?: Record<string, { contentIds?: string[] } | undefined> };
};

const blockId = (value: unknown) => {
  if (!value || typeof value !== "object") return undefined;
  const id = (value as { props?: { id?: unknown } }).props?.id;
  return typeof id === "string" && id ? id : undefined;
};

// Puck's replace walks every slot with `[...content].map(item => item.props)`.
// A hole becomes an explicit undefined entry in that copy and the walk throws.
// Compact those lists, and keep the zone index aligned with them, before saving.
const repairPuckLists = (state: PuckListState | undefined) => {
  if (!state?.data) return;
  const sync = (zoneKey: string, list: unknown[]) => {
    const clean = list.filter((item) => Boolean(blockId(item)));
    const zone = state.indexes?.zones?.[zoneKey];
    const ids = clean.map((item) => blockId(item) as string);
    if (zone && zone.contentIds?.join("\0") !== ids.join("\0")) zone.contentIds = ids;
    return clean;
  };
  const content = state.data.content;
  if (Array.isArray(content)) {
    const clean = sync("root:default-zone", content);
    if (clean.length !== content.length || clean.some((item, index) => item !== content[index])) {
      state.data.content = clean;
    }
  }
  for (const [key, list] of Object.entries(state.data.zones ?? {})) {
    if (!Array.isArray(list)) continue;
    const clean = sync(key, list);
    if (clean.length !== list.length || clean.some((item, index) => item !== list[index])) state.data.zones![key] = clean;
  }
  const compactContent = (blocks: unknown[]) => {
    blocks.forEach((block) => {
      const props = (block as { props?: { content?: unknown[] } } | undefined)?.props;
      const content = props?.content;
      if (!props || !Array.isArray(content)) return;
      const clean = content.filter((item) => Boolean(blockId(item)));
      if (clean.length !== content.length) props.content = clean;
      compactContent(clean);
    });
  };
  if (Array.isArray(state.data.content)) compactContent(state.data.content);
  Object.values(state.data.zones ?? {}).forEach((list) => { if (Array.isArray(list)) compactContent(list); });
};

/** Puck field onChange always writes to the *currently* selected block. */
export const useSafePuckFieldOnChange = <T,>(onChange: (value: T) => void) => {
  const getPuck = useGetPuck();
  const boundId = useRef<string | undefined>();
  if (boundId.current === undefined) boundId.current = getPuck().selectedItem?.props?.id;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  return useCallback((value: T) => {
    const api = getPuck() as { selectedItem?: { props?: { id?: string } } | null; __private?: { appState?: PuckListState } };
    const selectedId = api.selectedItem?.props?.id;
    if (!boundId.current || !selectedId || selectedId !== boundId.current) return false;
    repairPuckLists(api.__private?.appState);
    onChangeRef.current(value);
    return true;
  }, [getPuck]);
};

export const browserStyle = (style?: BlockStyle | string): CSSProperties | undefined => {
  const parsed = normalizeStyle(style);
  if (!parsed) return undefined;
  const families: Record<NonNullable<BlockStyle["fontFamily"]>, string> = {
    Courier: '"Courier New", Courier, monospace', Helvetica: "Arial, Helvetica, sans-serif",
    "Times-Roman": '"Times New Roman", Times, serif', Symbol: "Symbol, serif", ZapfDingbats: '"Zapf Dingbats", ZapfDingbats, sans-serif',
  };
  return {fontFamily: parsed.fontFamily ? families[parsed.fontFamily] : undefined, fontSize: parsed.fontSize ? `${parsed.fontSize}pt` : undefined, fontWeight: parsed.fontWeight, fontStyle: parsed.italic ? "italic" : undefined, textDecoration: parsed.underline ? "underline" : undefined, textAlign: parsed.textAlign, color: parsed.textColor, backgroundColor: parsed.backgroundColor, padding: parsed.padding, marginTop: parsed.marginTop, marginBottom: parsed.marginBottom};
};

/** Text properties only. Input labels, choices, and table cells set their own font-size, so the block wrapper's font-size never reaches them. */
export const browserTextStyle = (style?: BlockStyle | string): CSSProperties | undefined => {
  const parsed = browserStyle(style);
  if (!parsed) return undefined;
  return {fontFamily: parsed.fontFamily, fontSize: parsed.fontSize, fontWeight: parsed.fontWeight, fontStyle: parsed.fontStyle, textDecoration: parsed.textDecoration, textAlign: parsed.textAlign, color: parsed.color};
};

export const templateTextPreview = (value: string | ParagraphSegment[] | undefined, definitions: InputDefinitions) => {
  const values = new Map(paragraphVariables(definitions).map((item) => [item.variable, item.sample]));
  return parseTemplateText(value).map((part) =>
    "text" in part ? part.text : values.get(part.variable) ?? `{${part.variable}}`,
  ).join("");
};

export const paragraphPreview = (segments: ParagraphSegment[] | undefined, definitions: InputDefinitions) =>
  templateTextPreview(segments, definitions);
